import React, { useState, useEffect } from 'react';

interface SkewedBoxProps {
  children: React.ReactNode;
  className?: string;
  borderWidth?: number;
  borderColor?: string;
  variance?: number;
}

interface Point {
  x: number;
  y: number;
}

/**
 * A box component with randomly skewed quadrilateral borders.
 * Uses SVG to draw the border so it won't get clipped.
 */
const SkewedBox: React.FC<SkewedBoxProps> = ({
  children,
  className = '',
  borderWidth = 2,
  borderColor = 'black',
  variance = 0.06
}) => {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    // Generate random offset within variance range
    const getRandomOffset = () => {
      return (Math.random() - 0.5) * variance * 100;
    };

    // Generate four corners with slight random variations
    const corners = [
      { x: 0 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-left
      { x: 100 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-right
      { x: 100 + getRandomOffset(), y: 100 + getRandomOffset() }, // bottom-right
      { x: 0 + getRandomOffset(), y: 100 + getRandomOffset() } // bottom-left
    ];

    setPoints(corners);
  }, [variance]);

  // Convert percentage points to SVG polygon points string
  const svgPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div className={`relative ${className}`}>
      {/* SVG border overlay */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <polygon
          points={svgPoints}
          fill="none"
          stroke={borderColor}
          strokeWidth={borderWidth / 10} // Scale to viewBox coordinates
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Content layer */}
      <div className="relative p-8">
        {children}
      </div>
    </div>
  );
};

export default SkewedBox;
